import React from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Typography,
  Alert,
} from "@mui/material";
import type { TimeUnit } from "@/store/slices/line-chart-config-slice";
import { getRollingWindowOptionsByTimeUnit } from "@/utils/rolling-average-utils";

interface LineChartRollingAverageSectionProps {
  enabled: boolean;
  windowSize: number;
  timeUnit: TimeUnit;
  totalAvailablePeriods?: number;
  onEnabledChange: (enabled: boolean) => void;
  onWindowSizeChange: (size: number) => void;
  warningMessage?: string; // Rolling average warning from API (e.g., window exceeds available data)
  effectiveRollingPeriodCount?: number; // Actual periods used when partial window
  disabled?: boolean;
  onWindowExceedsDataChange?: (
    exceedsData: boolean,
    windowSize: number,
  ) => void;
  /**
   * When provided, restricts available window sizes to this list (from dataset guardrails).
   * Excludes 0 (No Rolling Average) — handled via guardrailNoRollingDisabled.
   */
  guardrailAllowedWindowSizes?: number[];
  /**
   * When true, "No Rolling Average" is not an allowed option — checkbox is forced on.
   */
  guardrailNoRollingDisabled?: boolean;
  /**
   * When true, no rolling-average window sizes are allowed by dataset policy —
   * checkbox is forced off and cannot be checked.
   */
  guardrailRollingDisabled?: boolean;
  /** Inline warning shown when the current config conflicts with dataset guardrails. */
  guardrailConflictWarning?: string;
}

const LineChartRollingAverageSection: React.FC<
  LineChartRollingAverageSectionProps
> = ({
  enabled,
  windowSize,
  timeUnit,
  totalAvailablePeriods = 0,
  onEnabledChange,
  onWindowSizeChange,
  warningMessage,
  effectiveRollingPeriodCount,
  disabled = false,
  onWindowExceedsDataChange,
  guardrailAllowedWindowSizes,
  guardrailNoRollingDisabled = false,
  guardrailRollingDisabled = false,
  guardrailConflictWarning,
}) => {
  const allWindowOptions = getRollingWindowOptionsByTimeUnit(timeUnit);
  const windowOptions = guardrailAllowedWindowSizes
    ? allWindowOptions.filter((o) =>
        guardrailAllowedWindowSizes.includes(o.value),
      )
    : allWindowOptions;

  // Check if rolling window exceeds available data
  const windowExceedsData =
    enabled && totalAvailablePeriods > 0 && windowSize > totalAvailablePeriods;

  // Notify parent when window exceeds data state changes
  React.useEffect(() => {
    if (onWindowExceedsDataChange) {
      onWindowExceedsDataChange(windowExceedsData, windowSize);
    }
  }, [windowExceedsData, windowSize, onWindowExceedsDataChange]);

  // Get the default window size for the selected timeUnit
  const getDefaultWindowSize = (): number => {
    switch (timeUnit) {
      case "quarter":
        return 2;
      case "semester":
        return 2;
      case "month":
        return 2;
      case "week":
        return 4;
      default:
        return 2;
    }
  };

  // Get helper text based on timeUnit
  const getHelperText = (): string => {
    const periodLabels: Record<TimeUnit, string> = {
      quarter: "quarter",
      semester: "semester",
      month: "month",
      week: "week",
    };
    const periodLabel = periodLabels[timeUnit];
    const preceding = windowSize - 1;
    const plural = preceding > 1 ? "s" : "";
    return `Each point shows the average of the current ${periodLabel} plus ${preceding} preceding ${periodLabel}${plural}`;
  };

  // Get period type name for warning messages
  const getPeriodTypeName = (): string => {
    const periodNames: Record<TimeUnit, string> = {
      quarter: "quarter",
      semester: "semester",
      month: "month",
      week: "week",
    };
    return periodNames[timeUnit];
  };

  const defaultSize = getDefaultWindowSize();
  const isDefault = windowSize === defaultSize;
  const periodType = getPeriodTypeName();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {guardrailConflictWarning && (
        <Alert severity="warning" sx={{ py: 1 }}>
          <Typography variant="body2">{guardrailConflictWarning}</Typography>
        </Alert>
      )}

      <FormControlLabel
        control={
          <Checkbox
            checked={
              guardrailRollingDisabled
                ? false
                : enabled || guardrailNoRollingDisabled
            }
            onChange={(e) => onEnabledChange(e.target.checked)}
            size="small"
            disabled={
              disabled || guardrailNoRollingDisabled || guardrailRollingDisabled
            }
          />
        }
        label={
          guardrailNoRollingDisabled
            ? "Apply Rolling Average (required by dataset policy)"
            : "Apply Rolling Average"
        }
      />

      {enabled && (
        <>
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Rolling Window Size
            </FormLabel>
            <Select
              value={windowSize}
              onChange={(e) => onWindowSizeChange(e.target.value as number)}
              disabled={disabled}
            >
              {windowOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{getHelperText()}</FormHelperText>
          </FormControl>

          {/* ⚠️ Warning: From API response (for other rolling average issues) */}
          {warningMessage && (
            <Alert severity="warning" sx={{ py: 1 }}>
              <Typography variant="body2">{warningMessage}</Typography>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

export default LineChartRollingAverageSection;
